import { create, tsx } from '@dojo/framework/core/vdom';
import { Link } from '@dojo/framework/routing/Link';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { FAQ } from '../../widgets/FAQ';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import { IndustryExpertise } from '../../widgets/IndustryExpertise';
import { Summary } from '../../widgets/Summary';
import SummaryList from '../../widgets/SummaryList';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Text from '../../widgets/Text';
import Image from '../../widgets/Image';
import ListColumns from '../../widgets/ListColumns';
import SitePenLogo from '../../widgets/SitepenLogo';

const consulting = require('../../assets/images/illustration/consulting.svg');
const compatibilityImg = require('../../assets/images/icon-illustration/cross-platform-development.svg');
const ensureQualityImg = require('../../assets/images/illustration/code-reuse.svg');
const functionalityImg = require('../../assets/images/icon-illustration/enterprise-software-ux-design.svg');
const performanceImg = require('../../assets/images/icon-illustration/custom-software.svg');
const qaBenefitsImg = require('../../assets/images/illustration/engineering.svg');
const securityImg = require('../../assets/images/icon-illustration/security-testing.svg');
const technologiesImg = require('../../assets/images/logo-illustration/qa-technologies.svg');
const usabilityImg = require('../../assets/images/icon-illustration/enterprise-app-ui-design.svg');

const factory = create();

export const QualityAssurance = factory(function QualityAssurance() {
	return (
		<div>
			<Head
				title="Software Quality Assurance &amp; Testing | SitePen"
				description="SitePen employs enterprise-grade software quality assurance and testing strategies throughout the entire development cycle to ensure customer satisfaction."
			/>

			<Hero background="level2PurpleTeal">
				{{
					xlTitle: <div>Quality Assurance</div>,
					blurb: 'Focusing on quality in every step of the development process.'
				}}
			</Hero>

			<Section>
				<ContentWrapper>
					<SummaryList
						trailingBias="medium"
						listGuttering="none"
						style="alternating"
						itemPaddingBottom="md"
					>
						{{
							items: [
								{
									title: 'Ensure the Quality of Your Web Application',
									blurb: (
										<virtual>
											Customers want your web applications to be fast and
											attractive, but they <Text italic>expect</Text> them to
											work flawlessly. Web applications have become the most
											common way that customers interact with you and your
											company. While many customers will tolerate some
											slowness in an application, they have little tolerance
											for sites that crash or lose their data. Assuring that
											your applications meet this rigorous standard demands
											that quality is considered throughout the application's
											development, not just as a final check at the end of the
											project. Gathering accurate requirements, breaking
											business needs into technical tasks, and thoroughly
											testing the application are all essential to ensuring
											the web application attains this goal.
										</virtual>
									),
									image: <Image src={ensureQualityImg} size="lg" />
								},
								{
									title: 'Why Develop with SitePen',
									blurb: (
										<virtual>
											<p>
												At SitePen, we believe that quality is the single,
												most important aspect of any project we undertake.
												Our commitment to delivering bug-free solutions
												infuses every part of our development activities.
											</p>
											<p>
												You will start to experience this as soon as you
												reach out to us. Our Sales and Project Management
												teams work with you to understand your goals and
												expectations, identifying the characteristics that
												have the most significant influence on the
												application's quality.
											</p>
											<p>
												Our engineering team will then develop the solution
												with these goals in mind. They also create extensive
												suites of automated tests that ensure that the code
												continues to work as intended. Finally, we
												continuously monitor the project in search of
												refactoring opportunities to improve the code's
												structure as it evolves. While not usually
												considered an essential aspect of quality assurance,
												we have found that superior code organization often
												leads to exceptional quality in the final product.
											</p>
										</virtual>
									),
									image: <SitePenLogo />
								},
								{
									blurb: (
										<virtual>
											<p>
												As the application approaches completion, we will
												shift the project to examine the quality of the
												solution. During this phase, we conduct tests that
												exercise all aspects of the complete solution. We
												verify the business logic's correctness, ensure the
												code is performant and will not bog down under
												real-world workloads, and harden the application
												against the vast array of security threats that
												today's applications face.
											</p>
											<p>
												Naturally, the final decision concerning the quality
												of the application resides with you. We will work
												with your infrastructure team to deploy the solution
												to a staging environment that allows you to conduct
												your own quality assurance verification. When all
												findings have been identified, we will resolve them,
												ensuring that you receive the most reliable and
												accurate application possible.
											</p>
										</virtual>
									),
									image: <Image src={consulting} lazy size="lg" />
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>

			<Section shaded>
				<ContentWrapper>
					<SummaryList>
						{{
							title: 'Software Quality Assurance Services',
							blurb: (
								<virtual>
									We utilize multiple testing strategies to verify the quality of
									our solutions.
								</virtual>
							),
							items: [
								{
									title: 'Functionality Testing',
									blurb: (
										<p>
											Functionality testing is intended to guarantee that the
											application performs as intended. It focuses on the
											solution's correctness and is typically tested against
											the product requirements identified at the beginning of
											the project and refined during its development.
											Functionality testing generally is verified through a
											combination of{' '}
											<Link to="servicesAutomatedTesting">
												automated tests
											</Link>
											, which codify the application's business rules, and
											manual testing by experienced quality assurance
											personnel.
										</p>
									),
									image: (
										<Image
											src={functionalityImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Usability Testing',
									blurb: `Many projects are functionally correct but end up as failures. These failures are often caused by poorly designed user-experiences that hamper adoption. Usability testing serves to verify the assumptions made during the design of the application's interfaces and interactions (i.e., its UX) by engaging real users to test and evaluate how well the application guides them through their workflows.`,
									image: (
										<Image src={usabilityImg} lazy size="icon" align="center" />
									)
								},
								{
									title: 'Performance Testing',
									blurb: (
										<p>
											<Link to="servicesPerformanceTesting">
												Performance testing
											</Link>{' '}
											ensures that the application works quickly and
											efficiently to process user requests. The goal of this
											testing is not merely to blindly accelerate the
											application. Instead, performance testing focuses on the
											most common workflows, optimizing them to improve user
											efficiency and ensure that they can stay focused on the
											task at hand. Performance testing also extends beyond
											the single user to verify that the application can
											handle expected workloads without degrading the user
											experience and can scale as demands increase.
										</p>
									),
									image: (
										<Image
											src={performanceImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								},
								{
									title: 'Security Testing',
									blurb: `All applications have security concerns. While it is tempting to rely on the company firewall for internal business applications, almost every application has features that should only be accessed by a small group of qualified personnel. Security testing involves a comprehensive review of the application in its production environment. In addition to thoroughly probing the application for vulnerabilities, such testing often consists of verifying that the secondary systems, such as identity providers, are sufficiently hardened that they can't be used as an attack vector into the application.`,
									image: (
										<Image src={securityImg} lazy size="icon" align="center" />
									)
								},
								{
									title: 'Compatibility Testing',
									blurb: `Users have an extensive set of applications and platforms that they can use to access today's web applications. Compatibility testing is the process of verifying the functionality, usability, performance, and security of an application for every option that users have. `,
									image: (
										<Image
											src={compatibilityImg}
											lazy
											size="icon"
											align="center"
										/>
									)
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<Summary
						reverse
						trailingBias="medium"
						blurbGutter="medium"
						imageGutter="medium"
					>
						{{
							title: 'Technologies We Use',
							blurb: (
								<virtual>
									<p>
										SitePen has worked with all of the major quality control
										tools that support the development of today's web
										applications. Our engineering teams have developed deep
										expertise in the available technologies. They can readily
										assemble a suite of testing tools that support each
										project's quality requirements without overloading it with
										extraneous elements.
									</p>
									<ListColumns>
										{[
											'Selenium',
											'Postman',
											'Chai',
											'Jasmine',
											'Cypress',
											'Mocha',
											'Karma',
											'Sinon'
										]}
									</ListColumns>
								</virtual>
							),
							image: <Image src={technologiesImg} lazy size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<IndustryExpertise />
			</Section>
			<Section>
				<ContentWrapper size="small">
					<Summary stack paddingBottom="sm">
						{{
							image: <Image src={qaBenefitsImg} lazy size="lg" />,
							title: 'Benefits of Enterprise Quality Assurance',
							blurb: (
								<virtual>
									<Text type="body" as="span">
										One of the harsh realities of software development is that{' '}
									</Text>
									<Text type="body" italic as="span">
										someone
									</Text>
									<Text type="body" as="span">
										{' '}
										will find flaws in a software system. The goal and benefit
										of enterprise quality assurance are to ensure that the
										defects are found by the development and maintenance teams,
										not the customer. Each flaw or bug that makes it to the
										end-user erodes their confidence in the entire enterprise
										software ecosystem.{' '}
									</Text>
								</virtual>
							)
						}}
					</Summary>
					<Summary stack>
						{{
							title: 'Choosing the Right Software Quality Assurance Company',
							blurb: (
								<virtual>
									The companies that provide the best software quality assurance
									services consider quality from the beginning of the project
									through to final delivery and launch. Best-in-class quality
									assurance processes look to identify each project's critical
									elements that serve as the key quality-drivers during the sales
									and requirements gathering phases. During development,
									engineering teams should craft the software using robust code
									organization patterns and a comprehensive set of automated tests
									to ensure that quality targets are achieved. As the project
									nears its conclusion, the application should be exposed to a
									battery of software quality control tools that confirm that the
									product is functional, performant, secure, and easy to use.
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ConnectStrip />
			</Section>
			<Section trailing>
				<FAQ title="Quality Assurance FAQs">
					{[
						{
							title:
								'What Is the Difference Between Quality Control and Quality Assurance?',
							content: [
								<p>
									Quality control encompasses tools and practices that are focused
									on identifying errors that exist in a system. This is a vital
									practice for any engineering project to adopt, but it is
									typically insufficient for many enterprise applications.
								</p>,
								<p>
									Quality assurance is a broader concept that takes advantage of
									any tool or practice to prevent defects from making it to the
									customer. Quality assurance processes integrate throughout the
									entire development project, including requirements gathering,
									development, and final quality checks.
								</p>
							]
						},
						{
							title: 'What Is the Importance of Software Quality Assurance?',
							content: (
								<Text type="body">
									Software quality is directly tied to user confidence, so defects
									that consumers discover will decrease their satisfaction with,
									and willingness to use, the defective software. If the users are
									outside of the enterprise, customers will likely seek
									alternative solutions to defective software, often allowing
									competitors to gain market share. Within the enterprise, faulty
									software can trigger the creation of unofficial systems that
									attempt to meet the business need in a better way. These{' '}
									<Text type="body" italic as="span">
										shadow{' '}
									</Text>
									systems are often poorly developed and maintained leading to
									quality, maintenance, reporting, and scaling issues.
								</Text>
							)
						},
						{
							title: 'What Are the Steps in Quality Assurance?',
							content: (
								<virtual>
									<Text type="body">
										The steps involved in quality assurance are easy to identify
										- they are precisely the same steps as the product
										development process. QA is meant to consider quality
										throughout the project; it is not intended as another set of
										procedures that must be followed. There are four primary
										phases to a development project and, by extension, the QA
										process.
									</Text>
									<SummaryList
										style="stack"
										listGuttering="none"
										itemPaddingBottom="xxs"
									>
										{{
											items: [
												{
													title: () => (
														<Text type="body" bold as="span">
															Requirements gathering
														</Text>
													),
													blurb: (
														<Text type="body">
															QA during the requirements gathering
															phases seeks to capture the key
															indicators that will drive the project's
															quality. Additionally, the requirements
															are conveyed to the testing team to
															start developing the manual and
															automated testing strategies that will
															verify that each requirement is met.
														</Text>
													)
												},
												{
													title: () => (
														<Text type="body" bold as="span">
															Development
														</Text>
													),
													blurb: (
														<Text type="body">
															During the development phase, the QA
															process tasks engineering teams with
															building quality into the code that they
															are writing. Engineering teams will
															often use code reviews, automated tests,
															and code refactoring to continually
															refine the codebase.
														</Text>
													)
												},
												{
													title: () => (
														<Text type="body" bold as="span">
															Testing and verification
														</Text>
													),
													blurb: (
														<Text type="body">
															As the project nears its conclusion, the
															testing team will apply quality control
															tools to identify any defects that the
															product contains. The development team
															will analyze and resolve each issue.
														</Text>
													)
												},
												{
													title: () => (
														<Text type="body" bold as="span">
															Delivery
														</Text>
													),
													blurb: (
														<Text type="body">
															The final phase in the QA process is
															delivery. During this phase, end-users
															have the opportunity to exercise their
															standard workflows in an isolated
															environment. Any defects found in this
															phase are then corrected by the
															engineering team and serve as a final
															check to ensure that the solution is as
															error free as possible.
														</Text>
													)
												}
											]
										}}
									</SummaryList>
								</virtual>
							)
						},
						{
							title: 'What Are the Best Practices for Software Quality Assurance?',
							content: (
								<virtual>
									<p>
										While the QA process's tools and specific practices vary
										widely, based on individual project requirements, some
										traits are universally present.
									</p>
									<p>
										Quality assurance is most effective when multi-disciplinary
										teams are involved. When the team comprises project
										managers, developers, testers, business analysts, and actual
										users, the team can achieve a deeper understanding of what
										quality means for the project and how to achieve it.
									</p>
									<p>
										Understanding which tools and practices are being used is
										also vital to any QA process. There are far too many tools
										available for any QA process to adopt them all. Instead, a
										subset of the available tools must be selected to form a
										lean but robust net that balances resource requirements with
										the ability to identify and eliminate defects.
									</p>
								</virtual>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default QualityAssurance;
